import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FaPhone,
    FaEnvelope,
    FaFacebook,
    FaWhatsapp,
    FaInstagram,
    FaLinkedin,
    FaTelegram,
    FaPinterest,
    FaSnapchat,
    FaDiscord,
    FaReddit,
    FaYoutube,
    FaGlobe,
    FaMapMarkerAlt,
    FaPalette,
    FaArrowUp,
    FaUtensils,
    FaTimes,
    FaStar,
    FaClock,
    FaSearch,
    FaExclamationTriangle,
} from "react-icons/fa";
import { FaXTwitter, FaTiktok, FaThreads } from "react-icons/fa6";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

/**
 * RestaurantMenu.jsx — Royal Damask Edition
 * 
 * Deep crimson and maroon gradient with antique-gold damask pattern aesthetic
 */

// ---------------------------------------------------------------------------
// API config
// ---------------------------------------------------------------------------
const API_BASE_URL = "https://api.editezy.com/api/users/products/by-company-name";

// ---------------------------------------------------------------------------
// Social platform metadata
// ---------------------------------------------------------------------------
const SOCIAL_META = {
    whatsapp: { Icon: FaWhatsapp, label: "WhatsApp", color: "#25D366" },
    facebook: { Icon: FaFacebook, label: "Facebook", color: "#1877F2" },
    instagram: { Icon: FaInstagram, label: "Instagram", color: "#E1306C" },
    twitter: { Icon: FaXTwitter, label: "Twitter / X", color: "#000000" },
    x: { Icon: FaXTwitter, label: "X", color: "#000000" },
    youtube: { Icon: FaYoutube, label: "YouTube", color: "#FF0000" },
    linkedin: { Icon: FaLinkedin, label: "LinkedIn", color: "#0A66C2" },
    telegram: { Icon: FaTelegram, label: "Telegram", color: "#26A5E4" },
    pinterest: { Icon: FaPinterest, label: "Pinterest", color: "#E60023" },
    snapchat: { Icon: FaSnapchat, label: "Snapchat", color: "#FFFC00" },
    tiktok: { Icon: FaTiktok, label: "TikTok", color: "#25F4EE" },
    threads: { Icon: FaThreads, label: "Threads", color: "#000000" },
    discord: { Icon: FaDiscord, label: "Discord", color: "#5865F2" },
    reddit: { Icon: FaReddit, label: "Reddit", color: "#FF4500" },
    website: { Icon: FaGlobe, label: "Website", color: null },
    googlemaps: { Icon: FaMapMarkerAlt, label: "Directions", color: "#34A853" },
};

const SOCIAL_ORDER = [
    "whatsapp",
    "instagram",
    "facebook",
    "twitter",
    "x",
    "threads",
    "youtube",
    "tiktok",
    "linkedin",
    "telegram",
    "pinterest",
    "snapchat",
    "discord",
    "reddit",
    "website",
    "googlemaps",
];

const getSocialMeta = (key) =>
    SOCIAL_META[key.toLowerCase()] ?? { Icon: FaGlobe, label: key, color: null };

// ---------------------------------------------------------------------------
// API → component data normalization
// ---------------------------------------------------------------------------
const socialTitleToKey = (title = "") => title.toLowerCase().replace(/[^a-z0-9]/g, "");

const normalizeApiResponse = (apiResponse) => {
    const company = apiResponse?.company || {};
    const products = Array.isArray(apiResponse?.products) ? apiResponse.products : [];

    const social = {};
    (company.socialLinks || []).forEach(({ title, link }) => {
        if (!title || !link) return;
        social[socialTitleToKey(title)] = link;
    });

    return {
        companyName: company.companyName || "Our Restaurant",
        cuisine: company.description || "",
        hours: "",
        rating: null,
        image: company.image || "",
        mobile: company.mobileNumber || "",
        email: company.email || "",
        social,
        products: products.map((p) => ({
            category: p.categoryId?.name || "Menu",
            name: p.name,
            image: p.image,
            price: p.price,
            description: p.description,
        })),
    };
};

// ---------------------------------------------------------------------------
// Damask Pattern Theme - Deep Crimson & Antique Gold
// ---------------------------------------------------------------------------
const THEME = {
    id: "royal-damask",
    label: "Royal Damask",
    swatch: "#D4AF37",
    stops: ["#1A0505", "#2D0A0A", "#4A1212"],
    bg: "from-[#1A0505] via-[#2D0A0A] to-[#4A1212]",
    accent: "#D4AF37",
    accent2: "#F4D03F",
    accentSoft: "rgba(212,175,55,0.32)",
    text: "text-amber-400",
    border: "border-amber-400/25",
    // Damask pattern SVG as a repeating background
    damaskPattern: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cdefs%3E%3Cpattern id='damask' width='200' height='200' patternUnits='userSpaceOnUse'%3E%3Crect width='200' height='200' fill='none'/%3E%3Cg stroke='%23D4AF37' stroke-width='0.8' fill='none' opacity='0.12'%3E%3Ccircle cx='100' cy='100' r='30'/%3E%3Ccircle cx='100' cy='100' r='45'/%3E%3Ccircle cx='100' cy='100' r='60'/%3E%3Cpath d='M100 40 L100 160 M40 100 L160 100'/%3E%3Cpath d='M58 58 L142 142 M58 142 L142 58'/%3E%3Cpath d='M70 45 L130 155 M45 70 L155 130 M70 155 L130 45 M45 130 L155 70'/%3E%3Ccircle cx='100' cy='40' r='6'/%3E%3Ccircle cx='100' cy='160' r='6'/%3E%3Ccircle cx='40' cy='100' r='6'/%3E%3Ccircle cx='160' cy='100' r='6'/%3E%3Ccircle cx='58' cy='58' r='5'/%3E%3Ccircle cx='142' cy='142' r='5'/%3E%3Ccircle cx='58' cy='142' r='5'/%3E%3Ccircle cx='142' cy='58' r='5'/%3E%3Ccircle cx='78' cy='78' r='3'/%3E%3Ccircle cx='122' cy='122' r='3'/%3E%3Ccircle cx='78' cy='122' r='3'/%3E%3Ccircle cx='122' cy='78' r='3'/%3E%3Ccircle cx='100' cy='78' r='4'/%3E%3Ccircle cx='100' cy='122' r='4'/%3E%3Ccircle cx='78' cy='100' r='4'/%3E%3Ccircle cx='122' cy='100' r='4'/%3E%3C/g%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23damask)'/%3E%3C/svg%3E")`,
};

const STORAGE_KEY = "premium-menu-theme";
const MIN_LOADING_DURATION = 1100;

const deriveDescription = (item) =>
    item.description ??
    `A signature ${item.category.toLowerCase()} preparation, crafted with hand-picked ingredients and finished tableside.`;

// ---------------------------------------------------------------------------
// Loading Screen
// ---------------------------------------------------------------------------
function LoadingScreen({ restaurantName }) {
    const theme = THEME;
    return (
        <motion.div
            key="loading-screen"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className={`fixed inset-0 z-[100] flex items-center justify-center bg-gradient-to-br ${theme.bg} overflow-hidden`}
        >
            {/* Damask pattern overlay */}
            <div
                className="absolute inset-0 opacity-20"
                style={{ backgroundImage: theme.damaskPattern }}
            />

            {/* Ambient glow blobs */}
            <div className="pointer-events-none absolute inset-0">
                <motion.div
                    className="absolute -top-32 -left-32 w-96 h-96 rounded-full blur-[100px]"
                    style={{ backgroundColor: theme.accent, opacity: 0.15 }}
                    animate={{ scale: [1, 1.15, 1] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full blur-[100px]"
                    style={{ backgroundColor: theme.accent2, opacity: 0.10 }}
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                />
            </div>

            <div className="relative z-10 flex flex-col items-center gap-6 px-6 text-center">
                {/* Rotating ring + centered utensil icon */}
                <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center">
                    <motion.span
                        className="absolute inset-0 rounded-full border-2"
                        style={{ borderColor: `${theme.accent}33`, borderTopColor: theme.accent }}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }}
                    />
                    <motion.span
                        className="absolute inset-2 rounded-full border"
                        style={{ borderColor: `${theme.accent2}33`, borderBottomColor: theme.accent2 }}
                        animate={{ rotate: -360 }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }}
                    />
                    <motion.div
                        animate={{ scale: [1, 1.12, 1] }}
                        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                        style={{ color: theme.accent }}
                    >
                        <FaUtensils size={26} />
                    </motion.div>
                </div>

                <div>
                    <motion.h2
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                        className="text-xl sm:text-2xl font-serif font-semibold tracking-tight text-white"
                    >
                        {restaurantName}
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className="mt-1.5 text-xs sm:text-sm tracking-[0.25em] uppercase text-amber-400/45"
                    >
                        Preparing the menu
                    </motion.p>
                </div>

                {/* Animated progress dots */}
                <div className="flex items-center gap-2">
                    {[0, 1, 2].map((i) => (
                        <motion.span
                            key={i}
                            className="w-2 h-2 rounded-full"
                            style={{ background: theme.accent }}
                            animate={{ opacity: [0.25, 1, 0.25], scale: [0.85, 1, 0.85] }}
                            transition={{
                                duration: 1.2,
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: i * 0.2,
                            }}
                        />
                    ))}
                </div>
            </div>
        </motion.div>
    );
}

// ---------------------------------------------------------------------------
// Error Screen
// ---------------------------------------------------------------------------
function ErrorScreen({ message, onRetry }) {
    const theme = THEME;
    return (
        <div
            className={`min-h-screen w-full flex items-center justify-center bg-gradient-to-br ${theme.bg} text-white px-6`}
        >
            <div
                className="absolute inset-0 opacity-10"
                style={{ backgroundImage: theme.damaskPattern }}
            />
            <div className="max-w-sm w-full text-center rounded-3xl border border-amber-400/20 bg-white/[0.05] backdrop-blur-2xl p-8 relative z-10">
                <div
                    className="mx-auto mb-4 w-14 h-14 rounded-full flex items-center justify-center"
                    style={{ background: `${theme.accent}22`, color: theme.accent }}
                >
                    <FaExclamationTriangle size={22} />
                </div>
                <h2 className="text-lg font-serif font-semibold mb-2 text-amber-400">
                    Couldn't load the menu
                </h2>
                <p className="text-sm text-white/50 mb-6">{message}</p>
                <button
                    onClick={onRetry}
                    className="px-5 py-2.5 rounded-full text-sm font-medium"
                    style={{
                        background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent2})`,
                        color: "#1A0505",
                    }}
                >
                    Try Again
                </button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function MenuPage({ data }) {
    const theme = THEME;
    const { companyName } = useParams();
    const [restaurant, setRestaurant] = useState(data && data.length ? data[0] : null);
    const [fetchError, setFetchError] = useState(null);
    const [fetchAttempt, setFetchAttempt] = useState(0);

    const [search, setSearch] = useState("");
    const [activeCategory, setActiveCategory] = useState("All");
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // -------------------------------------------------------------------
    // Fetch live data from the API
    // -------------------------------------------------------------------
    useEffect(() => {
        if (data && data.length) {
            setRestaurant(data[0]);
            return;
        }

        let cancelled = false;
        setFetchError(null);

        const fetchMenu = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/${encodeURIComponent(companyName)}`);
                if (!res.ok) {
                    throw new Error(`Request failed with status ${res.status}`);
                }
                const json = await res.json();
                if (!json?.success) {
                    throw new Error(json?.message || "Menu data unavailable");
                }
                if (!cancelled) {
                    setRestaurant(normalizeApiResponse(json));
                }
            } catch (err) {
                if (!cancelled) {
                    setFetchError(
                        err?.message || "Something went wrong while loading the menu."
                    );
                }
            }
        };

        fetchMenu();
        return () => {
            cancelled = true;
        };
    }, [data, companyName, fetchAttempt]);

    // Loading screen: hide once the hero image has preloaded
    useEffect(() => {
        if (!restaurant && !fetchError) return;
        if (fetchError) {
            setIsLoading(false);
            return;
        }

        let isMounted = true;
        const startedAt = Date.now();

        const finishLoading = () => {
            if (!isMounted) return;
            const elapsed = Date.now() - startedAt;
            const remaining = Math.max(MIN_LOADING_DURATION - elapsed, 0);
            setTimeout(() => {
                if (isMounted) setIsLoading(false);
            }, remaining);
        };

        if (restaurant?.image) {
            const preloadImg = new Image();
            preloadImg.src = restaurant.image;
            preloadImg.onload = finishLoading;
            preloadImg.onerror = finishLoading;
            const fallback = setTimeout(finishLoading, 4000);
            return () => {
                isMounted = false;
                clearTimeout(fallback);
            };
        } else {
            finishLoading();
        }

        return () => {
            isMounted = false;
        };
    }, [restaurant, fetchError]);

    // Lock background scroll while loading
    useEffect(() => {
        if (isLoading) {
            const prevOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = prevOverflow;
            };
        }
    }, [isLoading]);

    // Scroll listener
    useEffect(() => {
        const onScroll = () => setShowScrollTop(window.scrollY > 400);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const categories = useMemo(() => {
        if (!restaurant) return ["All"];
        const set = new Set(restaurant.products.map((p) => p.category));
        return ["All", ...Array.from(set)];
    }, [restaurant]);

    const filteredProducts = useMemo(() => {
        if (!restaurant) return [];
        return restaurant.products.filter((p) => {
            const matchesCategory =
                activeCategory === "All" || p.category === activeCategory;
            const matchesSearch = p.name
                .toLowerCase()
                .includes(search.trim().toLowerCase());
            return matchesCategory && matchesSearch;
        });
    }, [restaurant, activeCategory, search]);

    const socialLinks = useMemo(() => {
        const entries = Object.entries(restaurant?.social || {}).filter(
            ([, url]) => !!url
        );
        entries.sort(([a], [b]) => {
            const ia = SOCIAL_ORDER.indexOf(a.toLowerCase());
            const ib = SOCIAL_ORDER.indexOf(b.toLowerCase());
            const ra = ia === -1 ? SOCIAL_ORDER.length : ia;
            const rb = ib === -1 ? SOCIAL_ORDER.length : ib;
            return ra - rb;
        });
        return entries.map(([key, url]) => ({ key, url, ...getSocialMeta(key) }));
    }, [restaurant]);

    const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

    const retry = useCallback(() => {
        setRestaurant(null);
        setFetchError(null);
        setIsLoading(true);
        setFetchAttempt((n) => n + 1);
    }, []);

    // -------------------------------------------------------------------
    // Error state
    // -------------------------------------------------------------------
    if (fetchError && !restaurant) {
        return <ErrorScreen message={fetchError} onRetry={retry} />;
    }

    if (!restaurant) {
        return (
            <div className={`min-h-screen w-full bg-gradient-to-br ${theme.bg}`}>
                <LoadingScreen restaurantName="Loading Menu" />
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{restaurant.companyName} | Digital Menu</title>

                <meta
                    name="description"
                    content={
                        restaurant.cuisine ||
                        `Explore the delicious menu of ${restaurant.companyName}. Browse our latest dishes, prices, and contact details.`
                    }
                />

                <meta
                    name="keywords"
                    content={`${restaurant.companyName}, Restaurant Menu, Food Menu, Digital Menu, Online Menu, ${restaurant.companyName} Menu`}
                />

                <meta name="author" content={restaurant.companyName} />
                <meta name="robots" content="index, follow" />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={`${restaurant.companyName} | Digital Menu`} />
                <meta
                    property="og:description"
                    content={
                        restaurant.cuisine ||
                        `Browse the digital menu of ${restaurant.companyName}.`
                    }
                />
                <meta property="og:image" content={restaurant.image} />
                <meta property="og:site_name" content={restaurant.companyName} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content={`${restaurant.companyName} | Digital Menu`}
                />
                <meta
                    name="twitter:description"
                    content={
                        restaurant.cuisine ||
                        `Browse the digital menu of ${restaurant.companyName}.`
                    }
                />
                <meta name="twitter:image" content={restaurant.image} />

                {/* Theme */}
                <meta name="theme-color" content="#2D0A0A" />

                {/* Canonical */}
                <link rel="canonical" href={window.location.href} />
            </Helmet>

            
            <AnimatePresence>
                {isLoading && (
                    <LoadingScreen restaurantName={restaurant.companyName} />
                )}
            </AnimatePresence>

            <div
                className={`relative min-h-screen w-full bg-gradient-to-br ${theme.bg} transition-colors duration-[1200ms] ease-in-out font-sans text-white overflow-x-hidden`}
            >
                {/* ============================================================= */}
                {/* Layered atmosphere: damask pattern + aurora blobs             */}
                {/* ============================================================= */}
                <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
                    {/* Damask pattern overlay */}
                    <div
                        className="absolute inset-0 opacity-[0.08]"
                        style={{ backgroundImage: theme.damaskPattern }}
                    />

                    <motion.div
                        className="absolute -top-40 -left-40 w-[32rem] h-[32rem] rounded-full blur-[110px]"
                        style={{ backgroundColor: theme.accent, opacity: 0.12 }}
                        animate={{ x: [0, 40, -20, 0], y: [0, 30, -10, 0] }}
                        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <motion.div
                        className="absolute top-1/3 -right-40 w-[28rem] h-[28rem] rounded-full blur-[110px]"
                        style={{ backgroundColor: theme.accent2, opacity: 0.08 }}
                        animate={{ x: [0, -30, 20, 0], y: [0, -20, 30, 0] }}
                        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <motion.div
                        className="absolute bottom-0 left-1/4 w-[26rem] h-[26rem] rounded-full blur-[110px]"
                        style={{ backgroundColor: "#8B0000", opacity: 0.10 }}
                        animate={{ x: [0, 25, -25, 0], y: [0, -15, 15, 0] }}
                        transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.65)_100%)]" />

                    {/* Subtle vignette */}
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(26,5,5,0.7)_100%)]" />

                    <div className={`absolute bottom-0 left-0 w-full h-72 ${theme.bg} z-[1]`} />
                </div>

                {/* ============================================================= */}
                {/* Hero header                                                    */}
                {/* ============================================================= */}
                <header className="relative z-10">
                    <div className="relative h-[46vh] sm:h-[52vh] min-h-[300px] sm:min-h-[360px] w-full overflow-hidden">
                        <motion.img
                            src={restaurant.image}
                            alt={restaurant.companyName}
                            className="absolute inset-0 w-full h-full object-cover"
                            initial={{ scale: 1.15, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 1.4, ease: "easeOut" }}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src =
                                    `https://placehold.co/1200x800/2D0A0A/D4AF37?text=${restaurant.companyName}`;
                            }}
                        />
                        {/* Darkening overlays */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1A0505] via-[#1A0505]/65 to-[#1A0505]/25" />
                        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#1A0505] via-[#1A0505]/70 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-[#1A0505]/40" />
                        <div
                            className="absolute inset-0 opacity-30"
                            style={{
                                background: `radial-gradient(ellipse at 50% 100%, ${theme.accentSoft}, transparent 70%)`,
                            }}
                        />
                        {/* Damask overlay on hero */}
                        <div
                            className="absolute inset-0 opacity-[0.06]"
                            style={{ backgroundImage: theme.damaskPattern }}
                        />
                    </div>

                    <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-24 sm:-mt-32 relative">
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
                            className={`rounded-[1.5rem] sm:rounded-[2rem] border ${theme.border} bg-[#1A0505]/40 backdrop-blur-2xl shadow-2xl p-5 sm:p-8 lg:p-10`}
                            style={{ boxShadow: `0 30px 90px -30px ${theme.accentSoft}` }}
                        >
                            <div className="flex flex-col sm:flex-row sm:items-end gap-5 sm:gap-6">
                                <div className="flex items-center gap-4">
                                    <div
                                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden border shrink-0"
                                        style={{ borderColor: theme.accentSoft }}
                                    >
                                        <img
                                            src={restaurant.image}
                                            alt={restaurant.companyName}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = `https://placehold.co/600x600/2D0A0A/D4AF37?text=${restaurant.companyName}`;
                                            }}
                                        />
                                    </div>
                                    <div className="hidden sm:block h-16 w-px bg-amber-400/10" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <h1 className="text-2xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight leading-tight break-words text-amber-100">
                                        {restaurant.companyName}
                                    </h1>
                                    {restaurant.cuisine && (
                                        <p className="mt-1 text-sm text-amber-300/50 line-clamp-2">
                                            {restaurant.cuisine}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Contact row */}
                            <div className="mt-6 flex flex-wrap items-center gap-2.5 sm:gap-3">
                                {restaurant.mobile && (
                                    <a
                                        href={`tel:${restaurant.mobile}`}
                                        className="flex items-center gap-2 px-4 py-2 rounded-full text-xs sm:text-sm font-medium bg-amber-400/5 border border-amber-400/10 hover:bg-amber-400/10 transition-colors text-amber-200"
                                    >
                                        <FaPhone className="text-amber-400" /> {restaurant.mobile}
                                    </a>
                                )}
                                {restaurant.email && (
                                    <a
                                        href={`mailto:${restaurant.email}`}
                                        className="flex items-center gap-2 px-4 py-2 rounded-full text-xs sm:text-sm font-medium bg-amber-400/5 border border-amber-400/10 hover:bg-amber-400/10 transition-colors max-w-full truncate text-amber-200"
                                    >
                                        <FaEnvelope className="text-amber-400" />
                                        <span className="truncate">{restaurant.email}</span>
                                    </a>
                                )}
                            </div>

                            {/* Social row */}
                            {socialLinks.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-amber-400/10 flex flex-wrap items-center gap-2 sm:gap-2.5">
                                    <span className="text-[11px] font-semibold tracking-wider uppercase text-amber-400/40 mr-1">
                                        Connect with us:
                                    </span>
                                    {socialLinks.map(({ key, url, Icon, label, color }) => (
                                        <a
                                            key={key}
                                            href={url}
                                            target="_blank"
                                            rel="noreferrer"
                                            title={label}
                                            aria-label={label}
                                            className="group/social relative w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border border-amber-400/10 bg-[#D4AF37]/80 backdrop-blur-md transition-all duration-300 hover:scale-110 hover:border-transparent"
                                            style={{ color: color || theme.accent }}
                                            onMouseEnter={(e) => {
                                                if (color) {
                                                    e.currentTarget.style.backgroundColor = `${color}22`;
                                                    e.currentTarget.style.boxShadow = `0 0 16px 1px ${color}55`;
                                                } else {
                                                    e.currentTarget.style.boxShadow = `0 0 16px 1px ${theme.accentSoft}`;
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = "";
                                                e.currentTarget.style.boxShadow = "";
                                            }}
                                        >
                                            <Icon className="text-sm sm:text-base" />
                                        </a>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    </div>
                </header>

                {/* ============================================================= */}
                {/* Section heading                                                */}
                {/* ============================================================= */}
                <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 mt-12 sm:mt-14 mb-6 sm:mb-8">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <p
                            className="text-xs font-semibold tracking-[0.3em] uppercase mb-2"
                            style={{ color: theme.accent }}
                        >
                            Curated Selection
                        </p>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold tracking-tight">
                            <span
                                className="bg-clip-text text-transparent"
                                style={{
                                    backgroundImage: `linear-gradient(90deg, #F4D03F, ${theme.accent2})`,
                                }}
                            >
                                The Menu
                            </span>
                        </h2>
                        <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: 64 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="h-[3px] rounded-full mt-4"
                            style={{ background: theme.accent }}
                        />
                    </motion.div>
                </div>

                {/* ============================================================= */}
                {/* Category tabs                                                  */}
                {/* ============================================================= */}
                <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pb-8">
                    <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1">
                        {categories.map((cat) => {
                            const active = cat === activeCategory;
                            return (
                                <button
                                    key={cat}
                                    onClick={() => setActiveCategory(cat)}
                                    className={`relative shrink-0 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition-colors duration-300 ${active ? "text-[#1A0505]" : "text-amber-300/65 hover:text-amber-200"
                                        }`}
                                >
                                    {active && (
                                        <motion.span
                                            layoutId="category-indicator"
                                            className="absolute inset-0 rounded-full"
                                            style={{
                                                background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent2})`,
                                                boxShadow: `0 0 26px 2px ${theme.accentSoft}`,
                                            }}
                                            transition={{ type: "spring", stiffness: 400, damping: 32 }}
                                        />
                                    )}
                                    {!active && (
                                        <span className="absolute inset-0 rounded-full border border-amber-400/10 bg-[#1A0505]/20" />
                                    )}
                                    <span className="relative">{cat}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ============================================================= */}
                {/* Menu grid                                                      */}
                {/* ============================================================= */}
                <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pb-28">
                    <AnimatePresence mode="popLayout">
                        {filteredProducts.length === 0 ? (
                            <motion.p
                                key="empty"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="text-center text-amber-300/50 py-20"
                            >
                                No dishes available currently.
                            </motion.p>
                        ) : (
                            <motion.div
                                layout
                                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 sm:gap-6"
                            >
                                {filteredProducts.map((item, idx) => (
                                    <motion.div
                                        layout
                                        key={`${item.name}-${idx}`}
                                        initial={{ opacity: 0, y: 28 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -12 }}
                                        transition={{ duration: 0.45, delay: idx * 0.04 }}
                                        whileHover={{ y: -8 }}
                                        className="group relative rounded-[1.75rem] p-[1.5px] overflow-hidden"
                                        style={{
                                            background: `linear-gradient(150deg, ${theme.accentSoft}, transparent 55%, ${theme.accentSoft})`,
                                        }}
                                    >
                                        <div
                                            className="relative h-full rounded-[1.7rem] bg-[#1A0505]/40 backdrop-blur-2xl border border-amber-400/10 overflow-hidden flex flex-col transition-shadow duration-300"
                                            style={{
                                                boxShadow: "0 18px 45px -20px rgba(0,0,0,0.8)",
                                            }}
                                        >
                                            <div className="relative h-40 sm:h-44 overflow-hidden">
                                                <img
                                                    src={item.image}
                                                    alt={item.name}
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.15]"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = `https://placehold.co/600x600/2D0A0A/D4AF37?text=${item.name}`;
                                                    }}
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-[#1A0505]/80 via-[#1A0505]/10 to-transparent" />
                                                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out bg-gradient-to-r from-transparent via-amber-400/15 to-transparent pointer-events-none" />

                                                <span
                                                    className="absolute top-3 left-3 text-[10px] sm:text-[11px] font-semibold px-2.5 sm:px-3 py-1 rounded-full backdrop-blur-md max-w-[65%] truncate"
                                                    style={{
                                                        background: "rgba(26,5,5,0.7)",
                                                        color: theme.accent,
                                                        border: `1px solid ${theme.accentSoft}`,
                                                    }}
                                                >
                                                    {item.category}
                                                </span>
                                            </div>

                                            <div className="p-4 sm:p-5 flex flex-col gap-2 flex-1">
                                                <h3 className="font-medium text-sm sm:text-base lg:text-lg leading-snug text-amber-100">
                                                    {item.name}
                                                </h3>
                                                <p className="text-xs text-amber-300/45 leading-relaxed line-clamp-2">
                                                    {deriveDescription(item)}
                                                </p>
                                                <div className="mt-auto flex items-center justify-between gap-3 pt-3">
                                                    <span
                                                        className="text-base sm:text-lg font-semibold tracking-tight shrink-0"
                                                        style={{ color: theme.accent }}
                                                    >
                                                        ₹{item.price}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </main>

                {/* ============================================================= */}
                {/* Floating buttons                                              */}
                {/* ============================================================= */}
                <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-30 flex flex-col gap-3 items-end">
                    <AnimatePresence>
                        {showScrollTop && (
                            <motion.button
                                initial={{ opacity: 0, scale: 0.6 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.6 }}
                                onClick={scrollToTop}
                                className="relative w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center backdrop-blur-2xl border border-amber-400/10 bg-[#1A0505]/60 shadow-lg hover:scale-110 transition-transform overflow-hidden text-amber-400"
                                aria-label="Scroll to top"
                            >
                                <FaArrowUp />
                            </motion.button>
                        )}
                    </AnimatePresence>
                </div>

                <style>{`
                    .no-scrollbar::-webkit-scrollbar { display: none; }
                    .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                    .line-clamp-2 {
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                    }
                `}</style>
            </div>
        </>
    );
}